<template>
  <div class="upgrade-page">
    <div class="dashboard-hero">
      <div class="hero-glow"></div>
      <div class="hero-content">
        <h1 class="hero-title">{{ t('upgrade.hero.title') }}</h1>
        <p class="hero-subtitle">
          {{ t('upgrade.hero.subtitlePrefix') }}<strong>{{ t('upgrade.hero.subtitleOwner') }}</strong>{{ t('upgrade.hero.subtitleSuffix') }}
        </p>
        <p class="hero-note">
          {{ t('upgrade.hero.note') }}
        </p>
      </div>
      <div class="hero-plan" v-if="meuPlano">
        <span class="hero-plan-label">{{ t('upgrade.hero.currentPlan') }}</span>
        <strong class="hero-plan-name">{{ heroPlanTitle }}</strong>
        <span v-if="heroPlanSubtitle" class="hero-plan-subtitle">{{ heroPlanSubtitle }}</span>
      </div>
      <div class="hero-stats" v-if="meuPlano && heroMetrics">
        <div class="hero-stat">
          <span class="hero-stat-label">{{ t('upgrade.hero.statTask') }}</span>
          <span class="hero-stat-value">{{ heroMetrics.taskWs }}</span>
        </div>
        <div class="hero-stat">
          <span class="hero-stat-label">{{ t('upgrade.hero.statCrm') }}</span>
          <span class="hero-stat-value">{{ heroMetrics.crmWs }}</span>
        </div>
        <div class="hero-stat">
          <span class="hero-stat-label">{{ t('upgrade.hero.statUsers') }}</span>
          <span class="hero-stat-value">{{ heroMetrics.usersWs }}</span>
        </div>
      </div>
    </div>

    <div v-if="billingStatus?.hasSubscription" class="billing-status-banner">
      <p>
        {{ t('upgrade.billing.subscription') }} <strong>{{ billingStatusLabel }}</strong>
        <span v-if="billingRenewalLabel"> · {{ billingRenewalLabel }}</span>
        <span v-if="billingStatus?.cancelAtPeriodEnd" class="billing-cancel-hint"> · {{ t('upgrade.billing.cancelAtPeriodEnd') }}</span>
      </p>
      <button type="button" class="btn-portal" :disabled="upgrading" @click="abrirPortal">
        {{ t('upgrade.billing.manageSubscription') }}
      </button>
    </div>

    <div class="plans-grid">
      <div
        v-for="plano in planos"
        :key="plano.codigo"
        class="card plan-card"
        :class="{ 'plan-current': plano.codigo === meuPlano?.codigo }"
      >
        <div class="plan-card-main">
          <div class="plan-card-header">
            <div class="card-top">
            <div class="card-title-block">
              <h3>{{ cardTitle(plano) }}</h3>
              <p
                class="plan-card-tagline"
                :class="{ 'is-placeholder': !cardSubtitle(plano) }"
              >{{ cardSubtitle(plano) || '\u00a0' }}</p>
            </div>
            <span
              class="plan-pill"
              :class="getPlanPillClass(plano)"
            >
              <span class="plan-pill-icon-wrap" v-html="planPillIcon(plano)"></span>
              {{ getPlanPillLabel(plano) }}
            </span>
            </div>
            <p class="desc">{{ cardDescription(plano) }}</p>
          </div>
          <div class="feature-list-wrap">
            <ul class="feature-list">
              <template v-for="(row, idx) in getFeatureRows(plano)" :key="`${plano.codigo}-${idx}`">
                <li v-if="row.section" class="feature-section">
                  {{ row.section }}
                </li>
                <li
                  v-else-if="row.highlight === 'whatsapp'"
                  class="feature-row feature-row-whatsapp"
                  :class="featureRowClass(row.detail)"
                >
                  <div class="feature-whatsapp-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                  <div class="feature-whatsapp-body">
                    <div class="feature-whatsapp-top">
                      <span class="feature-detail-badge">{{ row.detail }}</span>
                      <span class="feature-whatsapp-title">{{ t('upgrade.whatsapp.title') }}</span>
                    </div>
                    <span class="feature-label feature-whatsapp-tagline">{{ row.tagline || row.label }}</span>
                  </div>
                </li>
                <li v-else class="feature-row" :class="featureRowClass(row.detail)">
                  <span class="feature-detail-badge">{{ row.detail }}</span>
                  <span class="feature-label">{{ row.label }}</span>
                </li>
              </template>
            </ul>
          </div>
          <div
            class="plan-price-block"
            :class="planPriceBlockClass(plano)"
          >
            <span
              v-if="planPrice(plano).eyebrow"
              class="plan-price-eyebrow"
            >{{ planPrice(plano).eyebrow }}</span>

            <template v-if="planPrice(plano).type === 'priced'">
              <div class="plan-price-amount-row">
                <span class="plan-price-currency">{{ planPrice(plano).currency }}</span>
                <span class="plan-price-value">{{ planPrice(plano).amount }}</span>
                <span class="plan-price-period">{{ planPrice(plano).period }}</span>
              </div>
              <p class="plan-price-note">{{ planPrice(plano).note }}</p>
            </template>

            <template v-else-if="planPrice(plano).type === 'contact'">
              <div class="plan-price-contact">
                <span class="plan-price-contact-title">{{ planPrice(plano).title }}</span>
                <span class="plan-price-contact-sub">{{ planPrice(plano).subtitle }}</span>
              </div>
            </template>

            <p v-else class="plan-price-fallback">{{ planPrice(plano).raw }}</p>
          </div>
        </div>
        <div class="plan-card-actions">
          <button
            v-if="!plano.corporateContactOnly"
            class="btn-primary"
            :disabled="upgrading || plano.codigo === meuPlano?.codigo || !isPlanoAssinavel(plano)"
            @click="iniciarCheckout(plano.codigo)"
          >
            {{ actionButtonLabel(plano) }}
          </button>
          <button
            v-else
            class="btn-contact"
            type="button"
            @click="falarComComercial"
          >
            {{ t('upgrade.actions.contactSales') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toastification'
import api from '../services/api'
import { getPlanDisplay } from '../constants/planDisplayCatalog'
import { getLocalizedPlanDisplay } from '../utils/planDisplayI18n'
import { usePlanoFeatures } from '../composables/usePlanoFeatures'
import { useAuthStore } from '../stores/auth'
import { useLocaleStore } from '../stores/locale'
import { formatDate } from '../utils/format'
import { trackMeta, trackPurchaseOnce } from '../utils/metaPixel'

const route = useRoute()
const router = useRouter()
const toast = useToast()
const authStore = useAuthStore()
const localeStore = useLocaleStore()
const { t } = useI18n()
const { loadMeuPlano, invalidateAll } = usePlanoFeatures()

const planos = ref([])
const meuPlano = ref(null)
const billingStatus = ref(null)
const upgrading = ref(false)

const PAID_PLANS = new Set(['START_VERIFICADO', 'PRO_VERIFICADO'])

const heroCatalog = computed(() => getLocalizedPlanDisplay(meuPlano.value?.codigo, t))

const heroPlanTitle = computed(() => {
  if (heroCatalog.value?.title) return heroCatalog.value.title
  return meuPlano.value?.nome || '—'
})

const heroPlanSubtitle = computed(() => heroCatalog.value?.subtitle || null)

const heroMetrics = computed(() => {
  if (heroCatalog.value?.heroMetrics) return heroCatalog.value.heroMetrics
  if (!meuPlano.value) return null
  return {
    taskWs: meuPlano.value.maxWorkspacesCriacaoLabel ?? '—',
    crmWs: '—',
    usersWs: '—'
  }
})

const carregar = async () => {
  const [planosResp, meuPlanoResp] = await Promise.all([
    api.get('/planos'),
    api.get('/planos/meu-plano')
  ])
  planos.value = planosResp.data || []
  meuPlano.value = meuPlanoResp.data || null
  await loadMeuPlano()
}

const carregarBillingStatus = async () => {
  try {
    const { data } = await api.get('/billing/stripe/status')
    billingStatus.value = data
  } catch {
    billingStatus.value = { configured: false, hasSubscription: false }
  }
}

const isPlanoAssinavel = (plano) => {
  if (!plano?.codigo) return false
  if (plano.codigo === 'FREE') return false
  return PAID_PLANS.has(plano.codigo)
}

const actionButtonLabel = (plano) => {
  if (plano.codigo === meuPlano.value?.codigo) return t('upgrade.actions.currentPlan')
  if (!isPlanoAssinavel(plano)) return '—'
  return t('upgrade.actions.subscribe')
}

const billingStatusLabel = computed(() => {
  const code = billingStatus.value?.planoCodigoPago
  if (!code) return billingStatus.value?.status || t('upgrade.billing.active')
  return getLocalizedPlanDisplay(code, t)?.title || code
})

const billingRenewalLabel = computed(() => {
  const raw = billingStatus.value?.currentPeriodEnd
  if (!raw) return ''
  try {
    const d = new Date(raw)
    return t('upgrade.billing.renewOn', { date: formatDate(d, localeStore.locale) })
  } catch {
    return ''
  }
})

const iniciarCheckout = async (planoCodigo) => {
  if (!isPlanoAssinavel({ codigo: planoCodigo })) return
  upgrading.value = true
  try {
    const { data } = await api.post('/billing/stripe/checkout-session', { planoCodigo })
    if (data?.planUpdated) {
      toast.success(t('upgrade.toast.planUpdated'))
      trackPurchaseOnce(`upgrade_${planoCodigo}_${Date.now()}`, planoCodigo, { content_category: 'upgrade' })
      invalidateAll()
      await carregar()
      if (authStore.user) {
        authStore.user.planoCodigo = meuPlano.value?.codigo
        localStorage.setItem('user', JSON.stringify(authStore.user))
      }
      return
    }
    if (data?.url) {
      window.location.href = data.url
      return
    }
    toast.error(t('upgrade.toast.paymentFailed'))
  } catch (err) {
    toast.error(err.response?.data?.error || t('upgrade.toast.checkoutError'))
  } finally {
    upgrading.value = false
  }
}

const abrirPortal = async () => {
  upgrading.value = true
  try {
    const { data } = await api.post('/billing/stripe/portal')
    if (data?.url) {
      window.location.href = data.url
    }
  } catch (err) {
    toast.error(err.response?.data?.error || t('upgrade.toast.portalError'))
  } finally {
    upgrading.value = false
  }
}

const tratarRetornoStripe = async () => {
  const stripe = route.query.stripe
  if (!stripe) return
  if (stripe === 'success') {
    toast.success(t('upgrade.toast.paymentSuccess'))
    invalidateAll()
    await carregar()
    await carregarBillingStatus()
    if (authStore.user && meuPlano.value?.codigo) {
      authStore.user.planoCodigo = meuPlano.value.codigo
      localStorage.setItem('user', JSON.stringify(authStore.user))
    }
    const planoCodigo = billingStatus.value?.planoCodigoPago || meuPlano.value?.codigo
    if (planoCodigo) {
      trackPurchaseOnce(route.query.session_id || 'stripe_success', planoCodigo)
    }
  } else if (stripe === 'cancel') {
    toast.info(t('upgrade.toast.checkoutCanceled'))
  }
  router.replace({ path: '/upgrade', query: {} })
}

const planDisplayFor = (plano) => getLocalizedPlanDisplay(plano.codigo, t)

const cardTitle = (plano) => planDisplayFor(plano)?.title ?? plano.nome
const cardSubtitle = (plano) => planDisplayFor(plano)?.subtitle ?? null
const cardDescription = (plano) => planDisplayFor(plano)?.description ?? plano.descricao
const cardPreco = (plano) => getPlanDisplay(plano.codigo)?.precoLabel ?? plano.precoLabel

const parseAmountNumber = (amount) => {
  const normalized = String(amount || '').replace(/\./g, '').replace(',', '.')
  const n = Number.parseFloat(normalized)
  return Number.isFinite(n) ? n : null
}

const planPrice = (plano) => {
  const label = String(cardPreco(plano) || '').trim()
  if (!label) {
    return { type: 'raw', raw: '—', eyebrow: t('upgrade.price.investment') }
  }

  if (plano.corporateContactOnly || /entrar em contato|contact|consulta|request/i.test(label)) {
    return {
      type: 'contact',
      eyebrow: null,
      title: t('upgrade.price.onRequest'),
      subtitle: t('upgrade.price.onRequestSub')
    }
  }

  const match = label.match(/^R\$\s*([\d.,]+)\s*(\/\s*.+)?$/i)
  if (match) {
    const amount = match[1]
    const period = (match[2] || t('upgrade.price.perMonth')).replace(/^\s*\/\s*/, '/ ')
    const value = parseAmountNumber(amount)
    const isFree = value === 0
    return {
      type: 'priced',
      currency: 'R$',
      amount,
      period,
      isFree,
      eyebrow: isFree ? t('upgrade.price.noMonthlyCost') : t('upgrade.price.monthlySubscription'),
      note: isFree ? t('upgrade.price.freeNote') : t('upgrade.price.paidNote')
    }
  }

  return { type: 'raw', raw: label, eyebrow: t('upgrade.price.investment') }
}

const planPriceBlockClass = (plano) => {
  const p = planPrice(plano)
  return {
    'plan-price-block--free': plano.codigo === 'FREE',
    'plan-price-block--start': plano.codigo === 'START_VERIFICADO',
    'plan-price-block--pro': plano.codigo === 'PRO_VERIFICADO',
    'plan-price-block--enterprise': plano.codigo === 'CORPORATIVO',
    'plan-price-block--contact': p.type === 'contact',
    'plan-price-block--priced-free': p.type === 'priced' && p.isFree,
    'plan-price-block--current': plano.codigo === meuPlano.value?.codigo
  }
}

const getFeatureRows = (plano) => {
  const rows = planDisplayFor(plano)?.featureRows
  if (rows?.length) return rows
  return fallbackFeatureRowsFromApi(plano)
}

const fallbackFeatureRowsFromApi = (plano) => [
  { section: t('upgrade.sections.apiSummary') },
  {
    label: t('upgrade.features.apiTaskCrm'),
    detail: `${plano.maxWorkspacesTask ?? '—'} / ${plano.maxWorkspacesCrm ?? '—'}`
  },
  { label: t('upgrade.features.apiMembers'), detail: plano.maxUsersPerWorkspace ?? '—' },
  {
    label: t('upgrade.features.apiWhatsapp'),
    detail: plano.features?.crmWhatsapp ? t('upgrade.detail.yes') : t('upgrade.detail.no')
  },
  {
    label: t('upgrade.features.apiLayoutBundle'),
    detail: plano.features?.taskEditLayout ? t('upgrade.detail.yes') : t('upgrade.detail.no')
  }
]

const featureRowClass = (detail) => {
  const d = String(detail || '').toLowerCase()
  const yes = t('upgrade.detail.yes').toLowerCase()
  const no = t('upgrade.detail.no').toLowerCase()
  if (d === yes || d === 'sim' || d === 'sí' || d === 'yes') return 'is-yes'
  if (d === no || d === 'não' || d === 'nao' || d === 'no') return 'is-no'
  return 'is-neutral'
}

const getPlanPillLabel = (plano) => {
  if (plano.codigo === meuPlano.value?.codigo) return t('upgrade.pills.current')
  if (plano.codigo === 'FREE') return t('upgrade.pills.free')
  if (plano.codigo === 'CORPORATIVO') return t('upgrade.pills.corporate')
  return t('upgrade.pills.paid')
}

const getPlanPillClass = (plano) => ({
  'plan-pill-current': plano.codigo === meuPlano.value?.codigo,
  'plan-pill-free': plano.codigo !== meuPlano.value?.codigo && plano.codigo === 'FREE',
  'plan-pill-paid': plano.codigo !== meuPlano.value?.codigo && plano.codigo !== 'FREE' && plano.codigo !== 'CORPORATIVO',
  'plan-pill-enterprise': plano.codigo !== meuPlano.value?.codigo && plano.codigo === 'CORPORATIVO'
})

/** Ícone SVG inline mínimo para o pill */
const planPillIcon = (plano) => {
  const current = plano.codigo === meuPlano.value?.codigo
  if (current) {
    return '<svg class="plan-pill-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>'
  }
  if (plano.codigo === 'FREE') {
    return '<svg class="plan-pill-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>'
  }
  if (plano.codigo === 'CORPORATIVO') {
    return '<svg class="plan-pill-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg>'
  }
  return '<svg class="plan-pill-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>'
}

const falarComComercial = () => {
  const email = import.meta.env.VITE_COMMERCIAL_EMAIL || 'comercial@backlog.app.br'
  const subject = encodeURIComponent(t('upgrade.actions.mailtoSubject'))
  window.open(`mailto:${email}?subject=${subject}`, '_blank')
}

onMounted(async () => {
  trackMeta('ViewContent', { content_name: 'Planos BackLog', content_category: 'pricing' })
  await carregarBillingStatus()
  await tratarRetornoStripe()
  await carregar()
})
</script>

<style scoped>
.upgrade-page {
  min-height: 100vh;
  padding: 24px;
  background: var(--bg-primary);
  color: var(--text-primary);
}
.billing-status-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  padding: 14px 18px;
  border-radius: 8px;
  border: 1px solid var(--border-primary);
  background: var(--bg-secondary);
}
.billing-status-banner p {
  margin: 0;
  font-size: 14px;
  color: var(--text-secondary);
}
.billing-cancel-hint {
  color: var(--warning, #e67e22);
}
.btn-portal {
  border: 1px solid var(--border-primary);
  background: transparent;
  color: var(--text-primary);
  padding: 8px 14px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
}
.btn-portal:hover:not(:disabled) {
  border-color: var(--accent-primary);
  color: var(--accent-primary);
}
.btn-portal:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.dashboard-hero {
  position: relative;
  overflow: hidden;
  border-radius: 4px;
  padding: 28px 24px 22px;
  margin-bottom: 18px;
  background: radial-gradient(circle at top, color-mix(in srgb, var(--accent-primary) 28%, transparent), transparent 60%),
              linear-gradient(180deg, var(--bg-secondary), var(--bg-primary));
  border: 1px solid var(--border-primary);
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.2);
}
.hero-glow {
  position: absolute;
  inset: -60px;
  background: radial-gradient(circle, color-mix(in srgb, var(--accent-primary) 18%, transparent), transparent 60%);
  pointer-events: none;
}
.hero-content {
  position: relative;
  z-index: 1;
  margin-bottom: 18px;
  padding-right: 220px;
}
.hero-title {
  font-size: 34px;
  line-height: 1.05;
  font-weight: 800;
  letter-spacing: -0.015em;
  margin: 0 0 8px;
}
.hero-subtitle {
  color: var(--text-secondary);
  margin: 0 0 10px;
  font-size: 16px;
  max-width: 52rem;
}
.hero-note {
  margin: 0;
  font-size: 13px;
  color: var(--text-secondary);
  opacity: 0.95;
  max-width: 48rem;
}
.hero-plan {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 1;
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
  background: linear-gradient(135deg, color-mix(in srgb, var(--accent-primary) 35%, transparent), color-mix(in srgb, var(--bg-secondary) 90%, transparent));
  border: 1px solid color-mix(in srgb, var(--accent-primary) 50%, var(--border-primary));
  border-radius: 4px;
  padding: 10px 14px;
  max-width: 200px;
}
.hero-plan-label {
  font-size: 10px;
  text-transform: uppercase;
  color: var(--text-secondary);
  letter-spacing: 0.8px;
}
.hero-plan-name {
  font-size: 16px;
  font-weight: 800;
  color: var(--text-primary);
}
.hero-plan-subtitle {
  font-size: 12px;
  font-weight: 600;
  color: color-mix(in srgb, var(--accent-primary) 70%, var(--text-secondary));
}
.hero-stats {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}
.hero-stat {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  padding: 10px 12px;
  text-align: center;
}
.hero-stat-label {
  display: block;
  font-size: 10px;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.7px;
  margin-bottom: 4px;
}
.hero-stat-value {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
}
.plan-card-header {
  flex-shrink: 0;
  min-height: 122px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 6px;
}

.desc {
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.45;
  margin: 0;
  min-height: calc(1.45em * 3);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  overflow: hidden;
}
.plans-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
  align-items: stretch;
}
.card {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  padding: 18px;
}
.plan-card {
  position: relative;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease;
  background: linear-gradient(180deg, var(--bg-secondary), var(--bg-primary));
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.plan-card-main {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.plan-card-actions {
  flex-shrink: 0;
  margin-top: 12px;
  padding-top: 4px;
}
.plan-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 18px 36px color-mix(in srgb, var(--accent-primary) 24%, transparent);
  border-color: color-mix(in srgb, var(--accent-primary) 55%, var(--border-primary));
}
.plan-current {
  border-color: color-mix(in srgb, var(--accent-primary) 60%, var(--border-primary));
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--accent-primary) 70%, transparent), 0 18px 38px color-mix(in srgb, var(--accent-primary) 26%, transparent);
}
.card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  min-height: 52px;
}

.card-title-block {
  flex: 1;
  min-width: 0;
}

.card-title-block h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 800;
  line-height: 1.2;
}

.plan-card-tagline {
  margin: 4px 0 0;
  min-height: 18px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
  color: color-mix(in srgb, var(--accent-primary) 65%, var(--text-secondary));
}

.plan-card-tagline.is-placeholder {
  visibility: hidden;
}
.plan-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  border: 1px solid var(--border-primary);
  padding: 4px 8px;
  font-size: 10px;
  font-weight: 700;
  color: var(--text-secondary);
  letter-spacing: 0.5px;
  flex-shrink: 0;
}
.plan-pill-current {
  border-color: color-mix(in srgb, var(--accent-primary) 55%, var(--border-primary));
  color: color-mix(in srgb, var(--accent-primary) 80%, var(--text-primary));
}
.plan-pill-paid {
  border-color: color-mix(in srgb, #22c55e 50%, var(--border-primary));
  color: color-mix(in srgb, #22c55e 70%, var(--text-primary));
}
.plan-pill-free {
  border-color: color-mix(in srgb, #64748b 45%, var(--border-primary));
  color: color-mix(in srgb, #64748b 85%, var(--text-primary));
}
.plan-pill-enterprise {
  border-color: color-mix(in srgb, #a855f7 50%, var(--border-primary));
  color: color-mix(in srgb, #a855f7 75%, var(--text-primary));
}
.plan-pill-icon-wrap {
  display: inline-flex;
  align-items: center;
}
.plan-pill-icon-wrap :deep(.plan-pill-svg) {
  width: 12px;
  height: 12px;
}
.plan-price-block {
  margin-top: 18px;
  padding: 18px 16px 16px;
  border-radius: 4px;
  border: 1px solid var(--border-primary);
  background: linear-gradient(
    165deg,
    color-mix(in srgb, var(--bg-secondary) 92%, transparent),
    color-mix(in srgb, var(--bg-primary) 98%, transparent)
  );
  text-align: center;
  box-shadow:
    0 8px 24px color-mix(in srgb, var(--accent-primary) 8%, transparent),
    inset 0 1px 0 color-mix(in srgb, #fff 6%, transparent);
}

.plan-price-block--current {
  border-color: color-mix(in srgb, var(--accent-primary) 45%, var(--border-primary));
  box-shadow:
    0 10px 28px color-mix(in srgb, var(--accent-primary) 14%, transparent),
    inset 0 1px 0 color-mix(in srgb, #fff 8%, transparent);
}

.plan-price-block--free,
.plan-price-block--priced-free {
  border-color: color-mix(in srgb, #22c55e 35%, var(--border-primary));
  background: linear-gradient(
    165deg,
    color-mix(in srgb, #22c55e 10%, var(--bg-secondary)),
    color-mix(in srgb, #22c55e 4%, var(--bg-primary))
  );
}

.plan-price-block--start {
  border-color: color-mix(in srgb, var(--accent-primary) 40%, var(--border-primary));
  background: linear-gradient(
    165deg,
    color-mix(in srgb, var(--accent-primary) 12%, var(--bg-secondary)),
    color-mix(in srgb, var(--accent-primary) 5%, var(--bg-primary))
  );
}

.plan-price-block--pro {
  border-color: color-mix(in srgb, #f59e0b 45%, var(--border-primary));
  background: linear-gradient(
    165deg,
    color-mix(in srgb, #f59e0b 14%, var(--bg-secondary)),
    color-mix(in srgb, #eab308 6%, var(--bg-primary))
  );
}

.plan-price-block--enterprise,
.plan-price-block--contact {
  margin-top: 14px;
  padding: 10px 12px;
  border-radius: 4px;
  border-color: var(--border-primary);
  background: color-mix(in srgb, var(--bg-secondary) 88%, transparent);
  box-shadow: none;
  text-align: left;
}

.plan-price-block--enterprise.plan-price-block--current,
.plan-price-block--contact.plan-price-block--current {
  border-color: color-mix(in srgb, var(--accent-primary) 35%, var(--border-primary));
  box-shadow: none;
}

.plan-price-eyebrow {
  display: block;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text-secondary);
  margin-bottom: 10px;
}

.plan-price-amount-row {
  display: flex;
  align-items: baseline;
  justify-content: center;
  flex-wrap: wrap;
  gap: 2px 8px;
  line-height: 1;
}

.plan-price-currency {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-secondary);
  transform: translateY(-2px);
}

.plan-price-value {
  font-size: 40px;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: var(--text-primary);
  font-variant-numeric: tabular-nums;
}

.plan-price-block--free .plan-price-value,
.plan-price-block--priced-free .plan-price-value {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.plan-price-block--start .plan-price-value {
  background: linear-gradient(135deg, var(--accent-primary) 0%, color-mix(in srgb, var(--accent-primary) 70%, #3b82f6) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.plan-price-block--pro .plan-price-value {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.plan-price-period {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-secondary);
  align-self: flex-end;
  margin-bottom: 5px;
}

.plan-price-note {
  margin: 12px 0 0;
  font-size: 11px;
  line-height: 1.45;
  color: var(--text-secondary);
  font-weight: 500;
}

.plan-price-contact {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.plan-price-block--contact .plan-price-contact-title {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0;
  color: var(--text-primary);
}

.plan-price-block--contact .plan-price-contact-sub {
  font-size: 11px;
  font-weight: 500;
  color: var(--text-secondary);
  line-height: 1.35;
}

.plan-price-fallback {
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: var(--text-primary);
}

.feature-list-wrap {
  margin-top: 4px;
}
.feature-list {
  list-style: none;
  padding: 0;
  margin: 0 0 8px;
  display: grid;
  gap: 6px;
}
.feature-section {
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-secondary);
  padding: 10px 0 4px;
  margin-top: 4px;
  border-bottom: 1px solid var(--border-primary);
}
.feature-row {
  font-size: 12px;
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
  background: color-mix(in srgb, var(--bg-secondary) 90%, transparent);
  border-radius: 8px;
  padding: 6px 8px;
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 8px;
}
.feature-detail-badge {
  font-size: 11px;
  font-weight: 700;
  min-width: 5rem;
  text-align: center;
  padding: 3px 6px;
  border-radius: 6px;
  border: 1px solid var(--border-primary);
}
.feature-row.is-yes .feature-detail-badge {
  background: color-mix(in srgb, #22c55e 14%, transparent);
  border-color: color-mix(in srgb, #22c55e 35%, var(--border-primary));
  color: color-mix(in srgb, #22c55e 85%, var(--text-primary));
}
.feature-row.is-no .feature-detail-badge {
  background: color-mix(in srgb, #ef4444 16%, transparent);
  border-color: color-mix(in srgb, #ef4444 45%, var(--border-primary));
  color: #ef4444;
}
.feature-row.is-neutral .feature-detail-badge {
  background: color-mix(in srgb, var(--accent-primary) 10%, transparent);
  color: var(--text-primary);
}
.feature-label {
  line-height: 1.35;
}

.feature-row-whatsapp {
  grid-template-columns: auto 1fr;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  margin-top: 4px;
  border: 1px solid color-mix(in srgb, #25d366 45%, var(--border-primary));
  background: linear-gradient(
    135deg,
    color-mix(in srgb, #25d366 12%, var(--bg-secondary)),
    color-mix(in srgb, #128c7e 8%, var(--bg-primary))
  );
  box-shadow:
    0 4px 14px color-mix(in srgb, #25d366 18%, transparent),
    inset 0 1px 0 color-mix(in srgb, #fff 8%, transparent);
}

.feature-row-whatsapp.is-yes {
  border-color: color-mix(in srgb, #25d366 55%, var(--border-primary));
}

.feature-row-whatsapp.is-no {
  border-color: color-mix(in srgb, #25d366 28%, var(--border-primary));
  opacity: 0.92;
}

.feature-whatsapp-icon {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  color: #fff;
  box-shadow: 0 6px 16px color-mix(in srgb, #25d366 35%, transparent);
}

.feature-whatsapp-icon svg {
  width: 28px;
  height: 28px;
}

.feature-whatsapp-body {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.feature-whatsapp-top {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.feature-whatsapp-title {
  font-size: 13px;
  font-weight: 800;
  color: #128c7e;
  letter-spacing: 0.01em;
}

.feature-whatsapp-tagline {
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.4;
}

.btn-primary {
  width: 100%;
  border: none;
  border-radius: 4px;
  background: var(--accent-primary, #5865f2);
  color: #fff;
  padding: 11px 14px;
  cursor: pointer;
  font-weight: 600;
}
.btn-primary:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.btn-contact {
  width: 100%;
  border: 1px solid var(--accent-primary);
  border-radius: 4px;
  color: var(--accent-primary);
  background: transparent;
  padding: 11px 14px;
  cursor: pointer;
  font-weight: 600;
}
@media (max-width: 768px) {
  .hero-title {
    font-size: 28px;
  }
  .hero-plan {
    position: relative;
    top: unset;
    right: unset;
    margin-bottom: 12px;
    max-width: none;
  }
  .hero-content {
    padding-right: 0;
  }
  .hero-stats {
    grid-template-columns: 1fr;
  }
}
</style>
